/*
 * aiClerk user_ask 独立层 ActionSheet 状态
 *
 * 原本 xhey_user_ask SSE 事件渲染成 chat 流卡片 (ChatPanel.tsx:1361-1374,
 * 使用 UserAskBlock).改造为底部 ActionSheet 独立层:
 *   - SSE 收到 xhey_user_ask → dispatch openActionSheet(完整 question)
 *   - chat 流只插一个轻量 chip part 标记"等待确认"
 *   - 用户在 ActionSheet 内完成选项确认 → onSubmit + dispatch close
 *
 * 不持久化 (ActionSheet 是短暂态;刷新页面后历史 chip 仍能看到选过什么,
 * 由 chat 流 user_ask_response 反查).
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

/**
 * SSE 收到的完整 question 原文 (xhey_user_ask 透传).
 * 字段对齐 useSSEChat.ts:966-1003 case 'user-ask' 里的 data 形态.
 * 跟 MessagePart.user-ask 形态稍有差异(后者用 camelCase),所以独立定义.
 */
export interface ActiveUserAskData {
  /** SSE delta 里的 pending_id (snake_case),给 chat 流 chip 关联用 */
  pendingId: string
  title: string
  description?: string
  /** UserAskQuestion[],透传给 UserAskBlock 渲染 */
  questions: any[]
  /** AI 已识别字段预览,可选 */
  autoFillPreview?: Array<{ label: string; value: string }>
}

export interface AiClerkUserAskState {
  /** ActionSheet 是否打开 */
  open: boolean
  /**
   * 显式打开的递增计数 — 每次 openActionSheet(SSE 新 ask / 用户点 chip 重开)+1。
   * ActionSheet 用它强制回到展开态:收起(collapsed)是组件内 CSS 隐藏,open 可能一直是
   * true 且 pendingId 不变,仅靠这俩无法感知"用户又点了一次前往确认"。
   */
  openTick: number
  /** 当前显示的 question 数据(null 表示无 active ask) */
  active: ActiveUserAskData | null
  /**
   * 已提交答案,按 pendingId 存(本会话内有效,不持久化)。
   * 用途:用户提交后再点 chip 重开 ActionSheet 时据此渲染「只读摘要」而不是编辑态,
   * 避免出现「已提交还带提交按钮」。
   */
  submittedByPid: Record<string, Record<string, any>>
  /**
   * 埋点用 — 本 sessionId 内每个 ask 的序号 (第几个 ask).
   * - openActionSheet 时累计:同 sessionId 新 pid → 序号 + 1;同 pid 重开 → 保留原序号
   * - sessionId 变化 → 整个 map 清空重计
   * 对应文档字段 ask_show.number2.
   */
  askIndexByPid: Record<string, number>
  /** 当前序号计数对应的 sessionId,变了就重置 askIndexByPid */
  countingSessionId: string
}

const INITIAL_STATE: AiClerkUserAskState = {
  open: false,
  openTick: 0,
  active: null,
  submittedByPid: {},
  askIndexByPid: {},
  countingSessionId: ''
}

const slice = createSlice({
  name: 'aiClerkUserAsk',
  initialState: INITIAL_STATE,
  reducers: {
    /** SSE 收到 xhey_user_ask → 打开 ActionSheet */
    openActionSheet: (
      state,
      action: PayloadAction<ActiveUserAskData & { sessionId?: string }>
    ) => {
      state.open = true
      state.openTick += 1
      const { sessionId, ...active } = action.payload
      state.active = active
      // 维护 ask 计数:同 session 新 pid → 累加;同 pid 重开(用户点 chip)→ 保留原序号
      const sid = sessionId || ''
      if (sid && sid !== state.countingSessionId) {
        state.countingSessionId = sid
        state.askIndexByPid = {}
      }
      const pid = active.pendingId
      if (pid && !(pid in state.askIndexByPid)) {
        const used = Object.values(state.askIndexByPid)
        const max = used.length > 0 ? Math.max(...used) : 0
        state.askIndexByPid[pid] = max + 1
      }
    },
    /** 用户提交或取消 → 关闭(不清 active 数据,留作 chip 摘要回查) */
    closeActionSheet: (state) => {
      state.open = false
    },
    /**
     * 记录某个 ask 的已提交答案(用户在 ActionSheet 点提交时调用)。
     * 之后重开同一 ask 就渲染只读摘要。
     */
    recordSubmittedAnswers: (
      state,
      action: PayloadAction<{
        pendingId: string
        answers: Record<string, any>
      }>
    ) => {
      if (action.payload.pendingId) {
        state.submittedByPid[action.payload.pendingId] = action.payload.answers
      }
    },
    /** 完全清掉(切换会话 / 卸载 work 页面时) */
    reset: (state) => {
      state.open = false
      state.openTick = 0
      state.active = null
      state.submittedByPid = {}
      state.askIndexByPid = {}
      state.countingSessionId = ''
    }
  }
})

export const {
  openActionSheet,
  closeActionSheet,
  recordSubmittedAnswers,
  reset
} = slice.actions

export default slice.reducer
